import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  DEFAULT_LANGUAGE,
  LANGUAGES,
  TRANSLATIONS,
  type LanguageCode,
  type LanguageMeta,
} from '@constants/translations';
import { useUpdateUserPreferences, useUserPreferences } from '@hooks/user/use-user-preferences';
import { isPatchLogPreviewPage } from '@utils/patch-log-preview';
import { isApprovalLinkPage } from '@utils/approval-link-page';

const STORAGE_KEY = 'jarvisLanguage';

const isLanguageCode = (value: string | null): value is LanguageCode =>
  value === 'id' || value === 'en' || value === 'zh';

const resolveInitialLanguage = (): LanguageCode => {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }
  const saved = localStorage.getItem(STORAGE_KEY);
  if (isLanguageCode(saved)) {
    return saved;
  }
  return DEFAULT_LANGUAGE;
};

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (language: LanguageCode) => void;
  t: (key: string) => string;
  meta: LanguageMeta;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * A stored language only means something for a signed-in user, and the
 * provider also wraps the login screen — so the request waits for a token
 * rather than firing a guaranteed 401 on every visit.
 */
const hasSession = (): boolean => typeof window !== 'undefined' && !!localStorage.getItem('accessToken');

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [language, setLanguageState] = useState<LanguageCode>(resolveInitialLanguage);

  // Issue 8 — the choice belongs to the person, not to the browser.
  // localStorage still holds it so the first paint never flashes the wrong
  // language; the profile value, when there is one, wins right after and is
  // what makes a new device or an incognito window come up right.
  // Not on the CMS preview page, which leaves the visitor's profile alone.
  const { data: preferences } = useUserPreferences({ enabled: hasSession() && !isPatchLogPreviewPage && !isApprovalLinkPage });
  const savePreference = useUpdateUserPreferences();
  const adoptedFromProfile = useRef(false);

  useEffect(() => {
    const stored = preferences?.language;
    if (!stored || adoptedFromProfile.current) {
      return;
    }
    // Adopt once per session. Without the latch, a refetch would keep undoing
    // a switch the user just made until the PUT lands.
    adoptedFromProfile.current = true;
    setLanguageState(stored);
  }, [preferences?.language]);

  /**
   * Changing the language is a user decision, so it is pushed to the profile.
   * The write is fire-and-forget: the switch has already happened locally and
   * a failed save costs cross-device sync, nothing the user is waiting on.
   */
  const setLanguage = useCallback(
    (next: LanguageCode) => {
      setLanguageState(next);
      // Any later profile answer must not overwrite a deliberate choice.
      adoptedFromProfile.current = true;
      if (hasSession()) {
        savePreference.mutate({ language: next });
      }
    },
    [savePreference]
  );

  useEffect(() => {
    if (!isPatchLogPreviewPage) localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.setAttribute('lang', language);
    window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language } }));
  }, [language]);

  const t = useMemo(() => {
    const dict = TRANSLATIONS[language];
    return (key: string) => dict[key] ?? TRANSLATIONS[DEFAULT_LANGUAGE][key] ?? key;
  }, [language]);

  const meta = useMemo(() => {
    return LANGUAGES.find((l) => l.code === language) ?? LANGUAGES[0];
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t, meta }), [language, setLanguage, t, meta]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};
